
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { SEO } from '../SEO';
import { LawBenefits } from '../LawBenefits';
import { LawCaseStudies } from '../LawCaseStudies';
import {
  ShieldCheck, Server, Lock, CheckCircle2,
  Scale, Minus, Plus, Cpu, Calculator, ArrowRight, MessageSquare, X, Check, Loader2, Network, Eye, Users
} from 'lucide-react';
import { Page } from '../../types';
import { SmartConsultant } from '../SmartConsultant';
import { LawFirmProcess } from '../LawFirmProcess';
import { LeadFormModal } from '../LeadFormModal';

const RedactionTerminal = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStep((prev) => (prev + 1) % 4), 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-700 bg-[#0F172A] mt-2 h-[320px] flex flex-col">
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="ml-4 text-xs font-mono text-gray-400">{t('law.terminal.log')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${step === 3 ? 'bg-green-500' : 'bg-indigo-500 animate-pulse'}`}></div>
          <span className={`text-[10px] font-mono uppercase ${step === 3 ? 'text-green-500' : 'text-indigo-400'}`}>{step === 3 ? t('law.terminal.status_secure') : t('law.terminal.status_processing')}</span>
        </div>
      </div>
      <div className="p-6 font-mono text-xs md:text-sm leading-loose overflow-hidden relative flex-1 text-indigo-100">
        <div className="text-gray-500">[14:20:01] {t('law.terminal.doc_opening')}</div>
        <div className={`transition-opacity duration-500 ${step >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-red-400 pl-4 border-l border-red-900/50 my-2">{t('law.terminal.detected')}</div>
        </div>
        <div className={`transition-opacity duration-500 ${step >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-yellow-400 mt-2">[14:20:02] {t('law.terminal.redacting')}</div>
        </div>
        <div className={`transition-all duration-500 mt-2 p-3 rounded border ${step >= 2 ? 'bg-black/40 border-gray-700' : 'opacity-0'}`}>
          {step === 3 ? <span className="text-green-400 animate-pulse">{t('law.terminal.result_masked')}</span> : <span>{t('law.terminal.result_raw')}</span>}
        </div>
      </div>
    </div>
  );
};

export const LawPage: React.FC<{ onNavigate?: (page: Page) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const subSectors = [
    { title: t('law.sectors.items.01.title'), id: "01", desc: t('law.sectors.items.01.desc'), highlight: t('law.sectors.items.01.highlight') },
    { title: t('law.sectors.items.02.title'), id: "02", desc: t('law.sectors.items.02.desc'), highlight: t('law.sectors.items.02.highlight') },
    { title: t('law.sectors.items.03.title'), id: "03", desc: t('law.sectors.items.03.desc'), highlight: t('law.sectors.items.03.highlight') },
    { title: t('law.sectors.items.04.title'), id: "04", desc: t('law.sectors.items.04.desc'), highlight: t('law.sectors.items.04.highlight') },
    { title: t('law.sectors.items.05.title'), id: "05", desc: t('law.sectors.items.05.desc'), highlight: t('law.sectors.items.05.highlight') },
    { title: t('law.sectors.items.06.title'), id: "06", desc: t('law.sectors.items.06.desc'), highlight: t('law.sectors.items.06.highlight') },
    { title: t('law.sectors.items.07.title'), id: "07", desc: t('law.sectors.items.07.desc'), highlight: t('law.sectors.items.07.highlight') },
    { title: t('law.sectors.items.08.title'), id: "08", desc: t('law.sectors.items.08.desc'), highlight: t('law.sectors.items.08.highlight'), variant: 'dark' }
  ];

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900">
      <SEO
        title={t('law.hero.title_line1') + ' ' + t('law.hero.title_line2')}
        description={t('law.hero.subtitle')}
        keywords="Anwaltssoftware KI, Legal Tech Österreich, Kanzlei Digitalisierung"
      />
      <section className="relative pt-20 pb-32 px-6 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 items-center relative z-10">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="inline-block px-4 py-2 bg-indigo-50 text-indigo-700 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-indigo-100">{t('law.hero.tagline')}</div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] mb-8">{t('law.hero.title_line1')} <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 to-violet-600">{t('law.hero.title_line2')}</span></h1>
              <p className="text-xl text-gray-500 leading-relaxed mb-10 max-w-xl">{t('law.hero.subtitle')}</p>
              <div className="flex flex-wrap gap-4 mb-12">
                <button onClick={() => setIsModalOpen(true)} className="bg-indigo-700 text-white px-8 py-4 font-bold uppercase tracking-widest text-xs hover:bg-indigo-800 transition-all shadow-xl rounded-sm">{t('law.hero.cta_audit')}</button>
                <button onClick={() => onNavigate && onNavigate('roi')} className="bg-white text-gray-900 border border-gray-200 px-8 py-4 font-bold uppercase tracking-widest text-xs hover:border-indigo-600 transition-all rounded-sm flex items-center gap-2"><Calculator size={16} /> {t('law.hero.cta_roi')}</button>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 relative flex items-center justify-center py-12"><Reveal delay={0.2}><RedactionTerminal /></Reveal></div>
        </div>
      </section>

      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">{t('law.sectors.title').split(' ').map((word, i, arr) => (
                <React.Fragment key={i}>
                  {word}{i !== arr.length - 1 && <br />}
                </React.Fragment>
              ))}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-gray-200">
            {subSectors.map((sector, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div
                  onClick={() => setIsModalOpen(true)}
                  className={`group p-12 border-b border-r border-gray-200 transition-all duration-500 min-h-[320px] flex flex-col justify-between relative cursor-pointer overflow-hidden h-full ${sector.variant === 'dark' ? 'bg-[#0B1121] text-white border-gray-800' : 'bg-white hover:bg-indigo-50/30'}`}
                >
                  <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"><span className={`${sector.variant === 'dark' ? 'text-clever-teal' : 'text-indigo-600'} text-xs font-bold uppercase tracking-widest`}>{sector.highlight}</span></div>
                  <div className={`text-xs font-mono mb-8 ${sector.variant === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>// {sector.id}</div>
                  <div>
                    <h3 className={`text-3xl font-bold mb-6 group-hover:translate-x-2 transition-transform ${sector.variant === 'dark' ? 'text-white' : 'text-gray-900'}`}>{sector.title}</h3>
                    <p className={`leading-relaxed text-lg ${sector.variant === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{sector.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-40 bg-[#0B1121] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-20">
              <div className="inline-block px-4 py-2 bg-indigo-900/30 text-indigo-300 text-xs font-bold uppercase tracking-[0.2em] mb-6 border border-indigo-500/30">{t('law.consultant.tagline')}</div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">{t('law.consultant.title')}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg">{t('law.consultant.subtitle')}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}><SmartConsultant context="law" title={t('law.consultant.bot_title')} subtitle={t('law.consultant.bot_subtitle')} suggestions={t('law.consultant.suggestions', { returnObjects: true }) as string[]} /></Reveal>
        </div>
      </section>

      <LawBenefits />
      <LawFirmProcess />
      <LawCaseStudies />

      <LeadFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} sector="Legal" />
    </div>
  );
};
